import { ShadowFieldStatus } from '@numeo/types-core'

type Tone = 'success' | 'primary' | 'danger' | 'neutral' | 'warning'

/** How each field status reads on the page: the word, the colour, and what it means (keyed by the shared enum, so a new status must be labelled here). */
export const SHADOW_STATUS: Record<ShadowFieldStatus, { label: string; tone: Tone; hint: string }> = {
    [ShadowFieldStatus.IDENTICAL]: { label: 'Identical', tone: 'success', hint: 'The same value on both sides.' },
    [ShadowFieldStatus.EQUIVALENT]: {
        label: 'Same fact',
        tone: 'primary',
        hint: 'Different text or number that the judge accepts as the same fact: case, spacing, "LLC", abbreviations, units, small tolerances.',
    },
    [ShadowFieldStatus.LEGACY_ONLY]: {
        label: 'Legacy only',
        tone: 'neutral',
        hint: 'Only the legacy record has a value; the judge accepts the pipeline leaving it empty.',
    },
    [ShadowFieldStatus.PIPELINE_ONLY]: { label: 'Pipeline only', tone: 'neutral', hint: 'Only the pipeline has a value. Never counted against it.' },
    [ShadowFieldStatus.LEGACY_MISSED]: {
        label: 'Legacy missed',
        tone: 'warning',
        hint: 'Only the pipeline found a value; the legacy record has none. The judge still counts it against the pipeline, but nothing conflicts.',
    },
    [ShadowFieldStatus.PIPELINE_MISSED]: {
        label: 'Pipeline missed',
        tone: 'warning',
        hint: 'Only the legacy record has a value; the pipeline found none. The judge counts it against the pipeline.',
    },
    [ShadowFieldStatus.DIFFERS]: {
        label: 'Differs',
        tone: 'danger',
        hint: 'Both sides have a value and the judge says they are not the same fact: a real conflict.',
    },
}

/** The order the statuses read in, best to worst. */
export const SHADOW_STATUS_ORDER = [
    ShadowFieldStatus.IDENTICAL,
    ShadowFieldStatus.EQUIVALENT,
    ShadowFieldStatus.LEGACY_ONLY,
    ShadowFieldStatus.PIPELINE_ONLY,
    ShadowFieldStatus.LEGACY_MISSED,
    ShadowFieldStatus.PIPELINE_MISSED,
    ShadowFieldStatus.DIFFERS,
]

/** A value as the page shows it: nothing as a dash, text as it is, anything else as compact JSON. */
export function formatShadowValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'string') return value
    return JSON.stringify(value)
}
